import type { ScheduledMode } from './multimodal/data.ts'
import type { ScheduledModeSet } from './multimodal/protocol.ts'
import type { LocationPoint } from './planning.ts'
import type { StationAccess } from './transit/router.ts'

export type WalkingAccessBackend = 'subway' | 'bus' | 'ferry' | 'multimodal'
export type AccessEndpoint = readonly [
  id: string,
  name: string,
  lat: number,
  lon: number,
  mode?: ScheduledMode,
]

interface WalkingAccessEntry {
  points: LocationPoint[]
  endpoints: AccessEndpoint[]
  modesByPerson: ScheduledModeSet[]
  result: Promise<StationAccess[][]>
}

export class SearchWalkingAccessCache {
  private readonly entries = new Map<WalkingAccessBackend, WalkingAccessEntry>()

  get(
    backend: WalkingAccessBackend,
    points: LocationPoint[],
    endpoints: AccessEndpoint[],
    modesByPerson: ScheduledModeSet[],
    load: () => Promise<StationAccess[][]>,
  ): Promise<StationAccess[][]> {
    const cached = this.entries.get(backend)
    if (cached && cached.endpoints === endpoints
      && samePoints(cached.points, points)
      && sameModes(cached.modesByPerson, modesByPerson)) return cached.result

    const result = load()
    const entry = {
      points: [...points],
      endpoints,
      modesByPerson: modesByPerson.map((modes) => [...modes] as ScheduledModeSet),
      result,
    }
    this.entries.set(backend, entry)
    void result.catch(() => {
      if (this.entries.get(backend) === entry) this.entries.delete(backend)
    })
    return result
  }
}

function samePoints(left: LocationPoint[], right: LocationPoint[]): boolean {
  return left.length === right.length && left.every((point, index) => point === right[index])
}

function sameModes(left: ScheduledModeSet[], right: ScheduledModeSet[]): boolean {
  return left.length === right.length && left.every((modes, personIndex) => {
    const other = right[personIndex]
    return modes.length === other.length && modes.every((mode, modeIndex) => mode === other[modeIndex])
  })
}
